import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Navigate, Route, Routes, useLocation } from 'react-router';

import { connect } from 'react-redux';
import { useAppSelector } from 'dux/app/hooks';

import isEmpty from 'lodash/isEmpty';

import { MetaData } from 'Components/MetaData';
import RequireAuth from 'Components/RequireAuth';

import { productsCategories } from 'constants/products';

import LoadingScene from 'Scenes/LoadingScene';

import {
  getCurrentlyLoadedHairQuestionSet,
  getCurrentlyLoadedSkinQuestionSet,
} from 'dux/consultation/selectors';
import * as featureFlagsSelectors from 'dux/featureFlags/selectors';
import { getNormalizedHaicareQuestions } from 'dux/questionSets/selectors';

import AcceptTermsContainer from './Containers/AcceptTermsContainer';
import ConnectedGoToDeciderContainer from './Containers/GoToDeciderContainer';
import HeroContainerV2 from './Containers/HeroContainerV2';
import ConnectedPosologyContainer from './Containers/PosologyContainer';
import ConnectedQuestionContainer from './Containers/QuestionContainer';
import ResultsContainer from './Containers/ResultsContainer';
import ScoringPrescriptionContainer from './Containers/ScoringPrescriptionContainer';
import ScoringSkincareContainer from './Containers/ScoringSkincareContainer';
import SkincareIngredientsContainer from './Containers/SkincareIngredientsContainer';
import SkincareResultsContainer from './Containers/SkincareResultsContainer';
import StylistContainer from './Containers/StylistContainer';
import { useConsultationCategory } from './hooks/useConsultationCategory';
import { useConsultationSubsetCategory } from './hooks/useConsultationSubsetCategory';
import hairWelcome from './questions/hair/hairWelcome';
import FirstQuestionLoading from './Scenes/FirstQuestionLoading';
import StylingGelConfirmationInterstitial from './Scenes/StylingGelConfirmationInterstitial';
import agnosticQuestionSet from './agnosticQuestionSet';
import { STYLING_GEL_CONSULTATION_QUESTION_SET_KEY } from './constants';

const absolute2Relative = route => route.split('/').splice(2).join('/');
const oldUrl = route => route.split('/').splice(1).join('/');
const prepareMetadataConsultation = pathname => {
  if (pathname?.includes(productsCategories.HAIRCARE)) {
    return {
      canonicalURL: '/consultation/haircare',
      description:
        'Answer the questions of our hair quiz to understand your hair type, needs and goals. Create your custom hair formula & routine with our free hair consultation.',
      title: 'Take Our Hair Quiz & Get Your Custom Hair Routine | Prose',
    };
  }
  if (pathname?.includes(productsCategories.SKINCARE)) {
    return {
      canonicalURL: '/consultation/skincare',
      description:
        'Try our skincare quiz! Share your unique skin needs, concerns and goals during your free skin care consultation and we’ll create your custom skincare routine.',
      title: 'Take Our Skincare Quiz & Get Your Custom Skin Routine | Prose',
    };
  }
  if (pathname?.includes('consultation')) {
    return { canonicalURL: '/consultation' };
  }
  return { canonicalURL: pathname };
};

const ConsultationRoutes = ({ isFlagsDoneFetching }) => {
  const consultationCategory = useConsultationCategory();
  const consultationSubsetCategory = useConsultationSubsetCategory();
  const { pathname, search } = useLocation();
  const routeParams = new URLSearchParams(search);
  const showDataDrivenConsultation = useAppSelector(
    featureFlagsSelectors.shouldShowDataDrivenConsultation
  );
  const normalizedHairQuestions = useAppSelector(getNormalizedHaicareQuestions);

  const hairQuestions = useAppSelector(state =>
    getCurrentlyLoadedHairQuestionSet(state, {
      consultationCategory,
      consultationSubsetCategory,
      routeParams,
    })
  );

  const skinQuestions = useAppSelector(state =>
    getCurrentlyLoadedSkinQuestionSet(state, {
      consultationCategory,
      consultationSubsetCategory,
      routeParams,
    })
  );

  const isFromStylingGelQuestionSet =
    consultationSubsetCategory === STYLING_GEL_CONSULTATION_QUESTION_SET_KEY;

  useEffect(() => {
    // here we use a webpack annotation to load those ressources in async mode
    import(/* webpackPrefetch: true */ '../Checkout/Routes').catch(() => {
      // ignore preload error since it's likelly network error
    });
  }, []);

  if (!isFlagsDoneFetching || (showDataDrivenConsultation && isEmpty(normalizedHairQuestions))) {
    return <LoadingScene />;
  }

  return (
    <Routes>
      <Route element={<MetaData {...prepareMetadataConsultation(pathname)} outlet />}>
        {/* Agnostic questions */}
        {agnosticQuestionSet.map((question, i) => (
          <Route
            key={question.route}
            element={
              <ConnectedQuestionContainer
                consultationCategory={consultationCategory}
                consultationSubsetCategory={consultationSubsetCategory}
                questionIndex={i}
                routeParams={routeParams}
              />
            }
            path={absolute2Relative(question.route)}
          />
        ))}

        {/* Haircare questions */}
        {hairQuestions?.map((question, i) => {
          return question.public ? (
            <Route
              key={question.route}
              element={
                <ConnectedQuestionContainer
                  consultationCategory={consultationCategory}
                  consultationSubsetCategory={consultationSubsetCategory}
                  questionIndex={i}
                  routeParams={routeParams}
                />
              }
              path={absolute2Relative(question.route)}
            />
          ) : (
            <Route
              key={question.route}
              element={
                <RequireAuth>
                  <StylistContainer>
                    <ConnectedQuestionContainer
                      consultationCategory={consultationCategory}
                      consultationSubsetCategory={consultationSubsetCategory}
                      questionIndex={i}
                      routeParams={routeParams}
                    />
                  </StylistContainer>
                </RequireAuth>
              }
              path={absolute2Relative(question.route)}
            />
          );
        })}
        <Route
          element={
            <HeroContainerV2
              consultationCategory={consultationCategory}
              consultationSubsetCategory={consultationSubsetCategory}
              routeParams={routeParams}
            />
          }
          path={absolute2Relative(hairWelcome.route)}
        />
        <Route path="haircare">
          {/* Loading screen to redirect to the first question of the consultation which is set in root.js  */}
          <Route element={<FirstQuestionLoading />} path="first-question" />
          <Route path="results">
            <Route
              element={
                <RequireAuth>
                  <StylingGelConfirmationInterstitial />
                </RequireAuth>
              }
              path="styling-gel-confirmation"
            />
            <Route
              element={
                <RequireAuth>
                  <ConnectedGoToDeciderContainer
                    consultationCategory={consultationCategory}
                    consultationSubsetCategory={consultationSubsetCategory}
                    routeParams={routeParams}
                  />
                </RequireAuth>
              }
              path="stored"
            />
            <Route
              element={
                <RequireAuth>
                  {isFromStylingGelQuestionSet ? (
                    <ResultsContainer
                      consultationCategory={consultationCategory}
                      consultationSubsetCategory={consultationSubsetCategory}
                      getReco
                      routeParams={routeParams}
                      successUrl="/consultation/haircare/results/styling-gel-confirmation"
                    />
                  ) : (
                    <ResultsContainer
                      consultationCategory={consultationCategory}
                      consultationSubsetCategory={consultationSubsetCategory}
                      routeParams={routeParams}
                      successUrl="/consultation/haircare/results/scoring"
                    />
                  )}
                </RequireAuth>
              }
              path="get-results"
            />
            <Route
              element={
                <RequireAuth>
                  <ScoringPrescriptionContainer consultationCategory={consultationCategory} />
                </RequireAuth>
              }
              path="scoring"
            />
            <Route
              element={
                <RequireAuth>
                  <ConnectedPosologyContainer consultationCategory={consultationCategory} />
                </RequireAuth>
              }
              path="ingredients"
            />
          </Route>
        </Route>

        {/* Skincare consultation */}
        {skinQuestions.map((question, i) => {
          return question.public ? (
            <Route
              key={question.route}
              element={
                <ConnectedQuestionContainer
                  consultationCategory={consultationCategory}
                  consultationSubsetCategory={consultationSubsetCategory}
                  questionIndex={i}
                  routeParams={routeParams}
                />
              }
              path={absolute2Relative(question.route)}
            />
          ) : (
            <Route
              key={question.route}
              element={
                <RequireAuth>
                  <StylistContainer>
                    <ConnectedQuestionContainer
                      consultationCategory={consultationCategory}
                      consultationSubsetCategory={consultationSubsetCategory}
                      questionIndex={i}
                      routeParams={routeParams}
                    />
                  </StylistContainer>
                </RequireAuth>
              }
              path={absolute2Relative(question.route)}
            />
          );
        })}
        <Route path="skincare">
          {/* Loading screen to redirect to the first question of the consultation which is set in root.js  */}
          <Route element={<FirstQuestionLoading />} path="first-question" />
          <Route path="results">
            <Route
              element={
                <RequireAuth>
                  <SkincareResultsContainer
                    consultationCategory={consultationCategory}
                    consultationSubsetCategory={consultationSubsetCategory}
                    routeParams={routeParams}
                    successUrl="/consultation/skincare/results/scoring"
                  />
                </RequireAuth>
              }
              path="get-results"
            />
            <Route
              element={
                <RequireAuth>
                  <ScoringSkincareContainer
                    consultationCategory={consultationCategory}
                    consultationSubsetCategory={consultationSubsetCategory}
                    routeParams={routeParams}
                  />
                </RequireAuth>
              }
              path="scoring"
            />
            <Route
              element={
                <RequireAuth>
                  <SkincareIngredientsContainer
                    consultationCategory={consultationCategory}
                    consultationSubsetCategory={consultationSubsetCategory}
                    routeParams={routeParams}
                  />
                </RequireAuth>
              }
              path="ingredients"
            />
            <Route
              element={
                <RequireAuth>
                  <ConnectedGoToDeciderContainer
                    consultationCategory={consultationCategory}
                    consultationSubsetCategory={consultationSubsetCategory}
                    routeParams={routeParams}
                  />
                </RequireAuth>
              }
              path="stored"
            />
          </Route>
        </Route>

        {/* Other routes */}
        <Route
          element={
            <RequireAuth>
              <AcceptTermsContainer
                consultationCategory={consultationCategory}
                consultationSubsetCategory={consultationSubsetCategory}
                routeParams={routeParams}
              />
            </RequireAuth>
          }
          path="accept-terms"
        />
        {/* @Marc: Starting with data driven cart at aug 2019, no need to have a loader */}
        <Route element={<Navigate replace to="/checkout/haircare" />} path="last-survey" />

        {/*
         * Temporary redirections to be removed after a few months from October 2022
         * Redirect users who use old URLs to the new ones
         */}
        {hairQuestions?.map(question => (
          <Route
            key={question.route}
            element={<Navigate replace to={question.route} />}
            path={oldUrl(absolute2Relative(question.route))}
          />
        ))}
        <Route path="results">
          <Route
            element={<Navigate replace to="/consultation/haircare/results/stored" />}
            path="stored"
          />
          <Route
            element={<Navigate replace to="/consultation/haircare/results/get-results" />}
            path="get-results"
          />
          <Route
            element={<Navigate replace to="/consultation/haircare/results/scoring" />}
            path="scoring"
          />
          <Route
            element={<Navigate replace to="/consultation/haircare/results/ingredients" />}
            path="ingredients"
          />
        </Route>
        <Route element={<Navigate replace to="/consultation" />} path="*" />
      </Route>
    </Routes>
  );
};

ConsultationRoutes.propTypes = {
  isFlagsDoneFetching: PropTypes.bool.isRequired,
};

const ConnectedConsultationRoutes = connect(state => ({
  isFlagsDoneFetching: featureFlagsSelectors.isFlagsDoneFetching(state),
}))(ConsultationRoutes);
export default ConnectedConsultationRoutes;
